import {CreditDecisionData, useCompany, useCreditDecisionMatrix, useUpdateCreditDecisionMatrix} from '@api'
import {Country, FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {errorAtom, isFormDirtyAtom, handleNavigateAtom, pageLinksAtom, getUserSelectionAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {
  createRadioBtnOptions,
  FormFieldItem,
  generateReadableErrorMessages,
  getUserData,
  useViewNavigate,
  validateFields,
} from 'common'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

/**
 * Initial data
 */
const initialCreditDecisionData: CreditDecisionData = {
  companyId: '',
  allowedCreditRating: '',
  maxExistingDebt: null,
  minimumIncome: null,
  requireNoActiveCollections: false,
}

export function useCreditDecisionFormModel() {
  const setError = useSetAtom(errorAtom)
  const setIsFormDirty = useSetAtom(isFormDirtyAtom)
  const handleNavigate = useSetAtom(handleNavigateAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [creditDecisionData, setCreditDecisionData] = useState<CreditDecisionData>(initialCreditDecisionData)
  const [creditDecisionFormFieldsErrors, setCreditDecisionFormFieldsErrors] = useState<string[]>([])
  const navigate = useViewNavigate()

  const {t} = useTranslation()
  const {id} = useParams()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const {data: creditDecisionResponse} = useCreditDecisionMatrix(userSelection?.companyId)
  const {data: companyData} = useCompany(userSelection.companyId)
  const {mutate: updateCreditiDecisionMutation} = useUpdateCreditDecisionMatrix()

  useEffect(() => {
    if (creditDecisionResponse) setCreditDecisionData(creditDecisionResponse)
  }, [creditDecisionResponse])

  useEffect(
    () =>
      setTitleLinks([
        {
          title: t(TranslationKey['Admin panel']),
          link: NavigationPathKey.ADMIN_PANEL,
        },
        {title: t(TranslationKey.Contracts), link: NavigationPathKey.CONTRACT_DASHBOARD},

        {title: t(TranslationKey['Credit Decision Configuration'])},
      ]),
    [],
  )

  const notRequiredFields: string[] = useMemo(() => {
    const companyCountry = companyData?.country
    switch (companyCountry) {
      case Country.SWEDEN:
        return []
      case Country.NORWAY:
        return []
      case Country.DENMARK:
        return []
      default:
        return []
    }
  }, [companyData])

  /**
   * A function handling Contract Template form fields change
   * @param event Change event
   */
  const onChangeCreditDecisionFormField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFormDirty(true)
    const fieldName = event.target.name
    const value = event.target.value
    setCreditDecisionFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    setCreditDecisionData(prev => ({...prev, [fieldName]: value}))
  }

  /**
   * Contract Template form fields configuration
   */
  const creditDecisionFormFields: FormFieldItem[] = useMemo(
    () =>
      [
        {
          fieldType: FormFieldType.INPUT,
          name: 'allowedCreditRating',
          label: t(TranslationKey['Allowed credit rating']),
          onChange: onChangeCreditDecisionFormField,
          value: creditDecisionData.allowedCreditRating,
          error: creditDecisionFormFieldsErrors.includes('allowedCreditRating'),
        },
        {
          fieldType: FormFieldType.INPUT,
          name: 'minimumIncome',
          label: t(TranslationKey['Minimum income']),
          onChange: onChangeCreditDecisionFormField,
          value: creditDecisionData.minimumIncome,
          error: creditDecisionFormFieldsErrors.includes('minimumIncome'),
        },
        {
          fieldType: FormFieldType.INPUT,
          name: 'maxExistingDebt',
          label: t(TranslationKey['Max existing debt']),
          onChange: onChangeCreditDecisionFormField,
          value: creditDecisionData.maxExistingDebt,
          error: creditDecisionFormFieldsErrors.includes('maxExistingDebt'),
        },
        {
          fieldType: FormFieldType.RADIO_BUTTON_GROUP,
          name: 'requireNoActiveCollections',
          label: t(TranslationKey['Require no active collections']),
          itemsInColumn: 1,
          value: String(creditDecisionData.requireNoActiveCollections),
          radioButtons: createRadioBtnOptions(
            'value',
            'name',
            'requireNoActiveCollections',
            () => false,
            (event: React.ChangeEvent<HTMLInputElement>) => onChangeCreditDecisionFormField(event),
            [
              {
                value: 'true',
                name: t(TranslationKey.Yes),
              },
              {
                value: 'false',
                name: t(TranslationKey.No),
              },
            ],
          ),
          error: creditDecisionFormFieldsErrors.includes('requireNoActiveCollections'),
        },
      ].filter(item => 'name' in item && !notRequiredFields.includes(item.name)),
    [notRequiredFields, creditDecisionFormFieldsErrors, creditDecisionData],
  )

  /**
   * A custom validation function for Contract Template form data
   * @returns If Contract Type form field containt any invalid fields
   */
  const validateData = (): boolean => {
    const invalidValues = validateFields(creditDecisionData).filter(item => !notRequiredFields.includes(item))

    const isFormInvalid = invalidValues.length > 0
    if (isFormInvalid) {
      setError({
        message: generateReadableErrorMessages(t, invalidValues),
        name: '',
      })
      setCreditDecisionFormFieldsErrors(invalidValues)
    }
    return isFormInvalid
  }

  /**
   * A function handling Save button click
   * If data is valid updated or saves a new Contract Template
   * @returns void
   */
  const onSubmit = () => {
    if (validateData()) return

    updateCreditiDecisionMutation(creditDecisionData, {
      onSuccess: () => {
        setIsFormDirty(false)
        navigate(NavigationPathKey.CREDIT_DECISION)
      },
    })
  }

  /**
   * A function handling Discard button click
   */
  const onDiscard = (): void => {
    handleNavigate(() => navigate(-1))
  }

  return {
    onDiscard,
    onSubmit,
    creditDecisionFormFields,
    t,
    creditDecisionData,
    creditDecisionFormFieldsErrors,
    id,
  }
}
